import { LOCALES, type Locale } from '@deskworks/shared-types';
import { Fragment, type ReactNode } from 'react';

/**
 * The web app in English and German (docs/features/languages.md).
 *
 * The English text is the key: `t('Sign out')` looks it up in the German dictionaries (src/locales/de/*.ts) and
 * falls back to the English text. The language is fixed for the page's lifetime — changing it reloads the page —
 * so `t()` also works in module-level constants.
 */

const KEY = 'deskworks.locale';

export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', de: 'Deutsch' };

const isLocale = (v: unknown): v is Locale => typeof v === 'string' && (LOCALES as readonly string[]).includes(v);

function stored(): Locale | null {
  try {
    const v = localStorage.getItem(KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

function store(next: Locale) {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* ignore */
  }
}

/** `?lang=de`: the landing page (deskworks.ai) hands its language to the app (app.deskworks.ai), another origin. */
function fromUrl(): Locale | null {
  const url = new URL(window.location.href);
  const lang = url.searchParams.get('lang');
  if (!isLocale(lang)) return null;
  store(lang);
  url.searchParams.delete('lang');
  window.history.replaceState(window.history.state, '', url);
  return lang;
}

function fromBrowser(): Locale {
  for (const tag of navigator.languages ?? [navigator.language]) {
    const lang = tag.slice(0, 2).toLowerCase();
    if (isLocale(lang)) return lang;
  }
  return 'en';
}

/** The page's language. */
export const locale: Locale = fromUrl() ?? stored() ?? fromBrowser();
/** The tag for Intl (dates, numbers, country names). */
export const intlLocale = locale === 'de' ? 'de-DE' : 'en-US';

document.documentElement.lang = locale;

const dictionaries = import.meta.glob<Record<string, string>>('../locales/de/*.ts', { import: 'default' });
const dict: Record<string, string> = locale === 'de' ? Object.assign({}, ...(await Promise.all(Object.values(dictionaries).map((load) => load())))) : {};

/** The text in the page's language; `{name}` placeholders take `vars`. */
export function t(text: string, vars?: Record<string, string | number>): string {
  const s = dict[text] ?? text;
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}

/**
 * `t()` for a text that means two things: `tc('verb', 'Open')` looks up the key `verb::Open` and falls back to the
 * English text.
 */
export function tc(context: string, text: string, vars?: Record<string, string | number>): string {
  const key = `${context}::${text}`;
  return t(key in dict ? key : text, vars);
}

/** A noun or label inside a sentence: lower case in English, as written in German (German nouns keep their capital). */
export const inSentence = (word: string) => (locale === 'de' ? word : word.toLowerCase());

/** Like `t()`, with elements in the placeholders: `tr('Click {run} to start', { run: <b>{t('Run now')}</b> })`. */
export function tr(text: string, parts: Record<string, ReactNode>): ReactNode {
  return t(text)
    .split(/(\{\w+\})/)
    .map((piece, i) => {
      const k = /^\{(\w+)\}$/.exec(piece)?.[1];
      return <Fragment key={i}>{k && k in parts ? parts[k] : piece}</Fragment>;
    });
}

/** Remembers the language for this browser; true when the page shows another one and has to load again. */
export function rememberLocale(next: Locale): boolean {
  store(next);
  return next !== locale;
}

/** Switches the language: the page loads again in it. */
export function setLocale(next: Locale) {
  if (rememberLocale(next)) window.location.reload();
}
